<template lang="pug">
  div(
    class="profile-api-keys"
    :class="{ empty: !accessTokens.length || loading }"
  )
    e-progress-circular(
      v-if="loading"
      size="xl"
      width="4"
    )
    div(
      v-else-if="!accessTokens.length"
      class="profile-api-keys__empty"
    )
      div(class="profile-api-keys__empty-icon mb-4")
        v-img(
          :src="require('~/assets/images/lock-with-key.png')"
          max-width="32"
        )
      h3(class="title-24px fw-500 mb-2") {{ $t('Create API key') }}
      div(class="text-14px light mb-8") {{ $t('To integrate Dubidoc into other systems') }}
      v-btn(
        class="secondary-button"
        @click="createKey"
      ) {{ $t('Create new key') }}
    div(
      v-else
      class="profile-api-keys__content"
    )
      div(class="profile-api-keys__org-id-card")
        div(class="text-12px light") {{ $t('Organization ID') }}
        div(class="profile-api-keys__org-id-info")
          div(class="profile-api-keys__org-id-value text-14px") {{ organizationId }}
          v-btn(
            icon
            @click.stop="copyOrganizationId"
          )
            e-svg-icon(
              :name="copiedOrgId ? 'copy-success-check-dark' : 'copy'"
            )
      div(class="profile-api-keys__table")
        div(class="profile-api-keys__table-header")
          div(class="profile-api-keys__table-column text-12px light") {{ $t('Name') }}
          div(class="profile-api-keys__table-column text-12px light") {{ $t('Secret key') }}
          div(class="profile-api-keys__table-column text-12px light text-center") {{ $t('Actions') }}
        div(class="profile-api-keys__table-body")
          div(
            v-for="item in accessTokens"
            :key="item.id"
            class="profile-api-keys__table-item"
          )
            div(class="profile-api-keys__table-row text-12px light dark") {{ item.title }}
            div(class="profile-api-keys__table-row text-12px light dark")
              | {{ formatApiKey(item.accessToken) }}
              v-btn(
                icon
                @click.stop="copyKey(item)"
              )
                e-svg-icon(
                  :name="copiedAccessTokenId === item.id ? 'copy-success-check-dark' : 'copy'"
                )
            div(class="profile-api-keys__table-row")
              t-orm-table-actions(
                :actions="actions"
                :item="item"
              )
</template>

<script>
import { mapGetters } from 'vuex'
import contentDialog from '~/mixins/dialogs/contentDialog'
import TOrmTableActions from '~/components/templates/orm/t-orm-table-actions'
import clipboard from '~/mixins/global/_clipboard'
import successSnackbar from '~/modules/snackbar/mixins/successSnackbar'
import EProgressCircular from '~/components/elements/progress/e-progress-circular'
import confirmationDialog from '~/mixins/dialogs/confirmationDialog'

export default {
  name: 'BlockProfileApiKeys',
  components: {
    TOrmTableActions,
    EProgressCircular
  },
  mixins: [contentDialog, confirmationDialog, clipboard, successSnackbar],
  data() {
    return {
      loading: false,
      copiedAccessTokenId: null,
      copiedOrgId: false
    }
  },
  computed: {
    ...mapGetters('accesstokens', {
      accessTokens: 'items'
    }),
    organizationId() {
      return this.$route.params.id
    },
    actions() {
      return [
        {
          name: 'edit',
          text: 'Edit name',
          icon: 'pen-edit',
          call: accessToken => this.editAccessTokenName(accessToken)
        },
        {
          name: 'delete',
          text: 'Delete key',
          icon: 'danger-trash',
          call: accessToken => this.deleteAccessToken(accessToken)
        }
      ]
    }
  },
  async created() {
    await this.getAccessTokens()
  },
  methods: {
    async getAccessTokens() {
      try {
        this.loading = true
        await this.$store.dispatch('accesstokens/getAll', {
          config: {
            headers: {
              'X-Organization': this.$route.params.id
            }
          }
        })
        // await AccessToken.api().all(
        //   {},
        //   {
        //     headers: {
        //       'X-Organization': this.$route.params.id
        //     }
        //   }
        // )
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.loading = false
      }
    },
    async createKey() {
      await this.contentDialog.open({
        width: '512px',
        component: 'block-profile-api-keys-form'
      })
    },
    async editAccessTokenName(item) {
      await this.contentDialog.open({
        width: '512px',
        component: 'block-profile-api-keys-form',
        componentProps: {
          isEdit: true,
          item
        }
      })
    },
    async deleteAccessToken(item) {
      await this.confirmationDialog.open({
        title: this.$t('Revoke key'),
        text: this.$t(
          'This API key will be disabled immediately. APl requests made using this key will be rejected. Once revoked, you will no longer be able to view or change this API key'
        ),
        buttons: {
          approve: {
            text: this.$t('Delete key')
          },
          dismiss: {
            text: this.$t('Cancel')
          }
        },
        icon: {
          src: require('~/assets/images/warning.png'),
          maxWidth: 80
        },
        onConfirm: async () => {
          await this.$store.dispatch('accesstokens/delete', { id: item.id })
          await this.successSnackbar.open({
            text: this.$t('API key has been successfully deleted')
          })
        }
      })
    },
    formatApiKey(str) {
      const slicedStr = str.slice(0, 20)

      return slicedStr.slice(0, 6) + '*'.repeat(slicedStr.length - 9) + str.slice(-3)
    },
    async copyKey(apiToken) {
      try {
        await this.copy(apiToken.accessToken)
        this.copiedAccessTokenId = apiToken.id
        await this.successSnackbar.open({
          text: this.$t('API key copied'),
          icon: 'copy-success-check'
        })
      } catch (e) {
        this.$handlers.error(e, this)
      }
    },
    async copyOrganizationId() {
      try {
        await this.copy(this.organizationId)
        this.copiedOrgId = true
        await this.successSnackbar.open({
          text: this.$t('Organization ID copied'),
          icon: 'copy-success-check'
        })
        setTimeout(() => {
          this.copiedOrgId = false
        }, 2000)
      } catch (e) {
        this.$handlers.error(e, this)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.profile-api-keys {
  width: 100%;

  &.empty {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;

    background-color: #ffffff;

    height: calc(100vh - 180px);
  }

  &__empty {
    max-width: 500px;
    width: 100%;
    text-align: center;

    &-icon {
      display: flex;
      align-items: center;
      justify-content: center;

      margin: 0 auto;

      width: 80px;
      height: 80px;

      border-radius: 24px;

      background: $background-secondary;
    }
  }
  &__content {
    width: 100%;
    background-color: #ffffff;
  }
  &__org-id-card {
    width: 100%;
    padding: 0px 16px;
    display: flex;
    flex-direction: column;
  }
  &__org-id-info {
    display: flex;
    align-items: center;
  }
  &__org-id-value {
    font-family: monospace;
    word-break: break-all;
    color: $text-dark;
  }

  &__table {
    width: 100%;
    background-color: #ffffff;
    border-radius: 16px;

    &-header {
      display: grid;
      grid-template-columns: 1fr 1fr 70px;
      border-bottom: 1px solid rgba(18, 18, 18, 0.06);
    }
    &-item {
      display: grid;
      grid-template-columns: 1fr 1fr 70px;
      overflow-x: auto;
    }
    &-column {
      padding: 16px;
    }
    &-row {
      display: flex;
      align-items: center;
      justify-content: space-between;

      padding: 16px;
      white-space: nowrap;
    }
  }
}
</style>
